import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { extend, useLoader, useFrame } from '@react-three/fiber'
import {
  useGLTF,
  Center,
  shaderMaterial,
  Sparkles,
  useAnimations,
} from '@react-three/drei'

import { TextureLoader } from 'three/src/loaders/TextureLoader'

import Pot from './elements/Pot'
import Horse from './elements/Horse'
import WardRobe from './elements/WardRobe'
import Gramophone from './elements/Gramophone'
import Television from './elements/Television'
import Room from './elements/Room'

import CameraControls from './CameraControls'

import { findItem } from '@/store/reducers/progressReducer'

import vertexShader from '@/webgl/shaders/scene/vertex.glsl?raw'
import fragmentShader from '@/webgl/shaders/scene/fragment.glsl?raw'

import finalVertexShader from '@/webgl/shaders/scene_final/vertex.glsl?raw'
import finalFragmentShader from '@/webgl/shaders/scene_final/fragment.glsl?raw'

const SceneShaderMaterial = shaderMaterial(
  {
    uTime: 0,
    uTexture1: null,
    uTexture2: null,
    uFactor1: 0,
    uFactor2: 0,
    uFactor3: 0,
    uFactor4: 0,
    uFactor5: 0,
  },
  vertexShader,
  fragmentShader,
)

const FinalSceneShaderMaterial = shaderMaterial(
  {
    uTexture1: null,
  },
  finalVertexShader,
  finalFragmentShader,
)

extend({ SceneShaderMaterial })

export default function Scene() {
  const dispatch = useDispatch()

  const { currentIndex, mode, hasFinished } = useSelector(
    (state) => state.progress,
  )

  const group = useRef()

  const { nodes, animations } = useGLTF('/models/scene/room.glb')
  const { actions } = useAnimations(animations, group)

  const interactiveObjects = [Gramophone, Horse, Television, Pot, WardRobe]

  const [bakedTexture, finalBakedTexture] = useLoader(TextureLoader, [
    '/textures/scene/dark.jpg',
    '/textures/scene/lit.jpg',
  ])
  bakedTexture.flipY = false
  finalBakedTexture.flipY = false

  const [outline, setOutline] = useState(false)
  const [material, setMaterial] = useState(
    new SceneShaderMaterial({
      uTexture1: bakedTexture,
      uTexture2: finalBakedTexture,
    }),
  )
  const finalMaterial = useRef(
    new FinalSceneShaderMaterial({
      uTexture1: finalBakedTexture,
    }),
  )

  useEffect(() => {
    if (!hasFinished) return
    setMaterial(finalMaterial.current)
  }, [hasFinished])

  const factors = [
    null,
    'uFactor1',
    'uFactor2',
    'uFactor3',
    'uFactor4',
    'uFactor5',
  ]

  useFrame((state, delta) => {
    // TODO - Will change this later to use Framer for better performances
    for (let i = 1; i <= 5; i++) {
      const factorName = factors[i]

      if (
        material &&
        currentIndex >= i &&
        material.uniforms &&
        material.uniforms[factorName] &&
        material.uniforms[factorName].value <= i
      ) {
        material.uniforms[factorName].value += 0.01
      }
    }

    if (material && material.uniforms && material.uniforms.uTime) {
      material.uniforms.uTime.value += delta
    }
  })

  const [sceneAnims, setSceneAnims] = useState([])

  useEffect(() => {
    setSceneAnims(Object.keys(actions).map((key) => actions[key]))
  }, [])

  function onItemClick(e, index) {
    e.stopPropagation()
    setOutline(false)

    if (currentIndex !== index || mode === 'inspection') return
    dispatch(findItem(index))

    document.body.style.cursor = 'default'
  }

  function onItemPointerEnter(e, index) {
    e.stopPropagation()
    if (currentIndex !== index || mode === 'inspection') return
    setOutline(true)

    document.body.style.cursor = 'pointer'
  }

  function onItemPointerLeave(e, index) {
    e.stopPropagation()
    if (currentIndex !== index || mode === 'inspection') return
    setOutline(false)

    document.body.style.cursor = 'default'
  }

  return (
    <>
      <CameraControls />
      <Center>
        <group ref={group} dispose={null}>
          <group name="Scene">
            {interactiveObjects.map((Item, index) => (
              <Item
                key={index}
                index={index}
                nodes={nodes}
                material={material}
                mode={mode}
                outline={outline}
                onClick={(e) => onItemClick(e, index)}
                onPointerEnter={(e) => onItemPointerEnter(e, index)}
                onPointerLeave={(e) => onItemPointerLeave(e, index)}
                sceneAnims={sceneAnims}
              />
            ))}
            <Room nodes={nodes} material={material} />
            <Sparkles
              scale={[10, 10, 10]}
              count={100}
              speed={0.5}
              opacity={0.05}
              size={5}
            />
          </group>
        </group>
      </Center>
    </>
  )
}
